import { AccessIntermediaryViewModel, useSearchForIntermediary } from 'bff';
import {
  Search,
  Button,
  NotificationBar,
  BUTTON_SIZE
} from '@sanlam-glacier/app-common/components';
import { useState } from 'react';
import { ProfileExpandingMenu } from '../profile-expanding-menu/ProfileExpandingMenu';
import { SearchResultHeader } from '../search-result-header/SearchResultHeader';
import { UserContextCard } from '../user-context-card/UserContextCard';
import { Block, Grid } from '@sanlam-glacier/app-common/modules/grid';
import styles from '../../TopBar.module.scss';
import clsx from 'clsx';

interface UserSelectMenuSearchProps {
  isLoading: boolean;
  selectedUserIntermediaryCode?: string;
  setSelectedUser: (user: AccessIntermediaryViewModel) => void;
}

export const UserSelectMenuSearch = ({
  isLoading,
  selectedUserIntermediaryCode,
  setSelectedUser
}: UserSelectMenuSearchProps) => {
  const [actingAsSearchOpen, setActingAsSearchOpen] = useState<boolean>(false);
  const [userSearchTerm, setUserSearchTerm] = useState<string>();

  const isSelected = (user: AccessIntermediaryViewModel) =>
    user.intermediaryCode === selectedUserIntermediaryCode;

  const {
    data: { data: proxyIntermediaryList } = {},
    mutate: searchIntermediaries,
    isPending: isLoadingGetAccess,
    isError,
    isSuccess
  } = useSearchForIntermediary({
    retry: false
  });

  const user = proxyIntermediaryList?.intermediaries.at(0);

  const handleSearchClick = () => {
    searchIntermediaries({
      intermediaryCode: userSearchTerm
    });
  };

  const hasError = isError || (isSuccess && proxyIntermediaryList?.intermediaries.length === 0);

  return (
    <ProfileExpandingMenu
      isOpen={actingAsSearchOpen}
      onExpandClick={() => {
        setActingAsSearchOpen(!actingAsSearchOpen);
      }}
      isLoading={isLoading}
    >
      <Grid row>
        <Block sm={9}>
          <Search
            placeholder='Search intermediary code'
            className={styles.noLgMargin}
            size={Search.SIZE.SMALL}
            onChange={(term) => setUserSearchTerm(term)}
            disabled={isLoadingGetAccess}
          />
        </Block>
        <Block sm={3}>
          <Button
            size={BUTTON_SIZE.SMALL}
            onClick={handleSearchClick}
            loading={isLoadingGetAccess}
            disabled={!userSearchTerm}
          >
            Search
          </Button>
        </Block>
      </Grid>
      <SearchResultHeader expectSingleResult={true} resultCount={user ? 1 : 0} />
      {user && !hasError && (
        <div className={styles.searchResults}>
          <UserContextCard
            userCode={user.intermediaryCode}
            firstName={user.firstName}
            surname={user.lastName}
            key={user.intermediaryCode}
            isSelected={isSelected(user)}
            selectUser={() => {
              if (!isSelected(user)) {
                setSelectedUser(user);
                setUserSearchTerm(undefined);
                setActingAsSearchOpen(false);
              }
            }}
          />
        </div>
      )}
      {hasError && (
        <div className={clsx(styles.errorBanner, styles.contextMenuExtraSidePadding)}>
          <NotificationBar
            variant={NotificationBar.VARIANT.SECONDARY}
            status={NotificationBar.STATUS.ERROR}
            highlight={false}
            roundedTop
            roundedBottom
            title={'Profile not found'}
            className={styles.errorBanner}
            subtitle={
              'We were unable to find the profile you were searching for. Please try again.'
            }
          />
        </div>
      )}
    </ProfileExpandingMenu>
  );
};
